import React from 'react';
import { Stage, Layer, Rect, Circle, Line, Text, Group, Transformer } from 'react-konva';
import { Button, ButtonGroup, Input, Label, FormGroup } from 'reactstrap';
import s from './LayoutCanvasEditor.module.scss';

const GRID = 20;
const SNAP = (v) => Math.round(v / GRID) * GRID;

function nextObjId(objects) {
    const max = objects.reduce((m, o) => Math.max(m, asNumber(o && o.id, 0)), 0);
    return max + 1;
}

const DEFAULT_COLORS = {
    table_rect: '#6d4c41',
    table_round: '#6d4c41',
    wall: '#78909c',
    text: '#ffffff',
    bar: '#455a64',
    seat: '#a1887f',
    chair: '#a1887f',
};

function normalizeType(type) {
    switch ((type || '').toLowerCase()) {
        case 'seat':
            return 'chair';
        case 'round_table':
        case 'roundtable':
            return 'table_round';
        case 'rect_table':
        case 'recttable':
        case 'table':
            return 'table_rect';
        default:
            return type || '';
    }
}

function asNumber(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeObject(obj) {
    const source = obj || {};
    const normalizedType = normalizeType(source.type || source.Type);
    return {
        ...source,
        id: source.id || source.Id,
        label: source.label != null ? source.label : source.Label,
        parentId: source.parentId != null ? source.parentId : source.ParentId,
        seatCount: asNumber(source.seatCount != null ? source.seatCount : source.SeatCount, 0),
        type: normalizedType,
        x: asNumber(source.x != null ? source.x : source.X, 0),
        y: asNumber(source.y != null ? source.y : source.Y, 0),
        width: Math.max(GRID, asNumber(source.width != null ? source.width : source.Width, GRID)),
        height: Math.max(GRID, asNumber(source.height != null ? source.height : source.Height, GRID)),
        rotation: asNumber(source.rotation != null ? source.rotation : source.Rotation, 0),
        fill: source.fill || source.Fill || DEFAULT_COLORS[normalizedType] || '#6d4c41',
    };
}

function unwrapLayoutPayload(raw) {
    let parsed = raw;
    for (let i = 0; i < 3 && typeof parsed === 'string'; i += 1) {
        try {
            parsed = JSON.parse(parsed);
        } catch (_) {
            break;
        }
    }
    return parsed;
}

function makeTable(objects, shape, x, y) {
    return {
        id: nextObjId(objects),
        type: shape === 'round' ? 'table_round' : 'table_rect',
        label: `T${objects.filter(o => o.type === 'table_rect' || o.type === 'table_round').length + 1}`,
        x: SNAP(x || 80),
        y: SNAP(y || 80),
        width: 60,
        height: 60,
        seatCount: 4,
        rotation: 0,
        fill: DEFAULT_COLORS[shape === 'round' ? 'table_round' : 'table_rect'],
        parentId: null,
    };
}

function makeWall(objects, x, y) {
    return {
        id: nextObjId(objects),
        type: 'wall',
        label: '',
        x: SNAP(x || 60),
        y: SNAP(y || 60),
        width: 120,
        height: 12,
        seatCount: 0,
        rotation: 0,
        fill: DEFAULT_COLORS.wall,
        parentId: null,
    };
}

function makeBar(objects, x, y) {
    return {
        id: nextObjId(objects),
        type: 'bar',
        label: 'Bar',
        x: SNAP(x || 60),
        y: SNAP(y || 60),
        width: 160,
        height: 40,
        seatCount: 0,
        rotation: 0,
        fill: DEFAULT_COLORS.bar,
        parentId: null,
    };
}

function makeText(objects, x, y) {
    return {
        id: nextObjId(objects),
        type: 'text',
        label: 'Label',
        x: SNAP(x || 80),
        y: SNAP(y || 80),
        width: 100,
        height: 24,
        seatCount: 0,
        rotation: 0,
        fill: DEFAULT_COLORS.text,
        parentId: null,
    };
}

function makeChair(objects, x, y) {
    return {
        id: nextObjId(objects),
        type: 'chair',
        label: '',
        x: SNAP(x || 80),
        y: SNAP(y || 80),
        width: 24,
        height: 24,
        seatCount: 1,
        rotation: 0,
        fill: DEFAULT_COLORS.chair,
        parentId: null,
    };
}

class LayoutCanvasEditor extends React.Component {
    constructor(props) {
        super(props);
        this.containerRef = React.createRef();
        this.stageRef = React.createRef();
        this.transformerRef = React.createRef();
        this.shapeRefs = {};
        this._suppressNextEmit = false;
        this._redrawAnimationFrame = null;
        this._redrawTimeout = null;
        this._resizeObserver = null;
        const savedShowGrid = localStorage.getItem('layoutEditor_showGrid');
        this.state = {
            containerWidth: 0,
            containerHeight: 0,
            selectedId: null,
            snap: true,
            showGrid: savedShowGrid === null ? true : savedShowGrid === 'true',
            objects: LayoutCanvasEditor.parseObjects(props.value),
        };
    }

    static parseObjects(raw) {
        try {
            const parsed = unwrapLayoutPayload(raw);
            const objects = Array.isArray(parsed)
                ? parsed
                : Array.isArray(parsed && parsed.objects)
                    ? parsed.objects
                    : Array.isArray(parsed && parsed.Objects)
                        ? parsed.Objects
                    : [];
            return objects.map(normalizeObject);
        } catch (_) {
            return [];
        }
    }

    componentDidMount() {
        this.measureContainer();
        if (typeof ResizeObserver !== 'undefined') {
            this._resizeObserver = new ResizeObserver(this.measureContainer);
            if (this.containerRef.current) {
                this._resizeObserver.observe(this.containerRef.current);
            }
        } else {
            window.addEventListener('resize', this.measureContainer);
        }
        this.scheduleStageRedraw();
    }

    componentDidUpdate(prevProps, prevState) {
        const { selectedId, objects } = this.state;

        // Attach transformer when selection changes
        if (selectedId !== prevState.selectedId) {
            this.attachTransformer(selectedId);
        }

        // If the parent value changed externally (e.g. tab switch back), re-sync
        if (prevProps.value !== this.props.value && this.props.value !== this._lastEmitted) {
            const parsed = LayoutCanvasEditor.parseObjects(this.props.value);
            this._suppressNextEmit = true;
            this.setState({
                objects: parsed,
                selectedId: parsed.some(o => o.id === selectedId) ? selectedId : null,
            }); // eslint-disable-line react/no-did-update-set-state
        }

        // Emit to parent when objects change
        if (objects !== prevState.objects) {
            if (this._suppressNextEmit) {
                this._suppressNextEmit = false;
                this.scheduleStageRedraw();
                return;
            }
            const json = JSON.stringify({ objects }, null, 2);
            this._lastEmitted = json;
            this.props.onChange(json);
        }

        if (
            prevProps.width !== this.props.width ||
            prevProps.height !== this.props.height ||
            prevProps.value !== this.props.value ||
            objects !== prevState.objects ||
            selectedId !== prevState.selectedId
        ) {
            this.scheduleStageRedraw();
        }
    }

    componentWillUnmount() {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
        } else {
            window.removeEventListener('resize', this.measureContainer);
        }
        if (this._redrawAnimationFrame) {
            cancelAnimationFrame(this._redrawAnimationFrame);
        }
        if (this._redrawTimeout) {
            clearTimeout(this._redrawTimeout);
        }
    }

    measureContainer = () => {
        const node = this.containerRef.current;
        if (!node) return;
        const containerWidth = node.clientWidth || node.offsetWidth || 0;
        const containerHeight = node.clientHeight || node.offsetHeight || 0;
        if (
            containerWidth > 0 &&
            containerHeight > 0 &&
            (
                containerWidth !== this.state.containerWidth ||
                containerHeight !== this.state.containerHeight
            )
        ) {
            this.setState({ containerWidth, containerHeight });
        }
    };

    scheduleStageRedraw() {
        if (this._redrawAnimationFrame) {
            cancelAnimationFrame(this._redrawAnimationFrame);
        }
        if (this._redrawTimeout) {
            clearTimeout(this._redrawTimeout);
        }

        const draw = () => {
            const stage = this.stageRef.current && this.stageRef.current.getStage
                ? this.stageRef.current.getStage()
                : this.stageRef.current;
            if (stage && typeof stage.batchDraw === 'function') {
                stage.batchDraw();
            }
        };

        this._redrawAnimationFrame = requestAnimationFrame(draw);
        this._redrawTimeout = setTimeout(draw, 180);
    }

    attachTransformer(selectedId) {
        const tr = this.transformerRef.current;
        if (!tr) return;
        if (!selectedId) {
            tr.nodes([]);
            tr.getLayer() && tr.getLayer().batchDraw();
            return;
        }
        const node = this.shapeRefs[selectedId] && this.shapeRefs[selectedId].current;
        if (node) {
            tr.nodes([node]);
            tr.getLayer() && tr.getLayer().batchDraw();
        }
    }

    getObjects() {
        return this.state.objects;
    }

    emitChange(objects) {
        this.setState({ objects });
    }

    addObject = (factory) => {
        const objects = this.getObjects();
        const newObj = factory(objects);
        const updated = [...objects, newObj];
        this.emitChange(updated);
        this.setState({ selectedId: newObj.id });
    };

    deleteSelected = () => {
        const { selectedId } = this.state;
        if (!selectedId) return;
        const updated = this.getObjects().filter(o => o.id !== selectedId && o.parentId !== selectedId);
        this.emitChange(updated);
        this.setState({ selectedId: null });
    };

    duplicateSelected = () => {
        const { selectedId } = this.state;
        if (!selectedId) return;
        const objects = this.getObjects();
        const src = objects.find(o => o.id === selectedId);
        if (!src) return;
        const newObj = { ...src, id: nextObjId(objects), x: src.x + GRID * 2, y: src.y + GRID * 2 };
        this.emitChange([...objects, newObj]);
        this.setState({ selectedId: newObj.id });
    };

    bringToFront = () => {
        const { selectedId } = this.state;
        if (!selectedId) return;
        const objects = this.getObjects();
        const idx = objects.findIndex(o => o.id === selectedId);
        if (idx < 0) return;
        const arr = [...objects];
        const [obj] = arr.splice(idx, 1);
        arr.push(obj);
        this.emitChange(arr);
    };

    sendToBack = () => {
        const { selectedId } = this.state;
        if (!selectedId) return;
        const objects = this.getObjects();
        const idx = objects.findIndex(o => o.id === selectedId);
        if (idx < 0) return;
        const arr = [...objects];
        const [obj] = arr.splice(idx, 1);
        arr.unshift(obj);
        this.emitChange(arr);
    };

    onDragEnd = (id, e) => {
        const { snap } = this.state;
        const node = e.target;
        let x = node.x();
        let y = node.y();
        if (snap) { x = SNAP(x); y = SNAP(y); node.position({ x, y }); }
        this.updateObject(id, { x, y });
    };

    onTransformEnd = (id, e) => {
        const node = e.target;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        node.scaleX(1);
        node.scaleY(1);
        // Group nodes have no intrinsic width/height (node.width() === 0), so we
        // must apply the transformer scale to the object's stored dimensions.
        const src = this.getObjects().find(o => o.id === id);
        const baseW = src ? asNumber(src.width, GRID) : GRID;
        const baseH = src ? asNumber(src.height, GRID) : GRID;
        const w = Math.max(GRID, Math.round(baseW * scaleX));
        const h = Math.max(GRID, Math.round(baseH * scaleY));
        const rotation = node.rotation();
        const { snap } = this.state;
        this.updateObject(id, {
            x: snap ? SNAP(node.x()) : node.x(),
            y: snap ? SNAP(node.y()) : node.y(),
            width: snap ? SNAP(w) : w,
            height: snap ? SNAP(h) : h,
            rotation,
        });
    };

    updateObject(id, fields) {
        const updated = this.getObjects().map(o => o.id === id ? { ...o, ...fields } : o);
        this.emitChange(updated);
    }

    updateSelectedField = (field, value) => {
        const { selectedId } = this.state;
        if (!selectedId) return;
        this.updateObject(selectedId, { [field]: value });
    };

    renderGrid(width, height) {
        const lines = [];
        for (let x = 0; x <= width; x += GRID) {
            lines.push(<Line key={`gx${x}`} points={[x, 0, x, height]} stroke="rgba(255,255,255,0.06)" strokeWidth={1} listening={false} />);
        }
        for (let y = 0; y <= height; y += GRID) {
            lines.push(<Line key={`gy${y}`} points={[0, y, width, y]} stroke="rgba(255,255,255,0.06)" strokeWidth={1} listening={false} />);
        }
        return lines;
    }

    renderObject = (obj) => {
        const shape = normalizeObject(obj);
        if (!this.shapeRefs[obj.id]) {
            this.shapeRefs[obj.id] = React.createRef();
        }
        const ref = this.shapeRefs[obj.id];
        const { selectedId } = this.state;
        const isSelected = selectedId === obj.id;
        const stroke = isSelected ? '#ffd600' : 'rgba(255,255,255,0.35)';
        const strokeWidth = isSelected ? 2 : 1;

        // Auto-seats persisted by the POS app. `obj.seats` is a count assigned
        // by the POS to a table/bar; `obj.detachedSeats` is a map of seatId ->
        // {x, y} in layout-space coordinates (used for bars where seats are
        // positioned individually). Render these as small read-only chair
        // markers so the editor matches what the POS shows.
        const seatCount = asNumber(obj && obj.seats, 0);
        const detached = obj && obj.detachedSeats && typeof obj.detachedSeats === 'object'
            ? obj.detachedSeats : null;
        const seatRadius = 6;
        const seatGap = 14;
        const seatFill = '#a1887f';
        const seatStroke = 'rgba(0,0,0,0.35)';
        const autoSeats = [];

        if (detached) {
            // Detached seats are stored in absolute layout coordinates; convert
            // to coordinates relative to this object's group origin.
            Object.keys(detached).forEach((seatId) => {
                const pos = detached[seatId];
                if (!pos) return;
                const sx = asNumber(pos.x, 0) - shape.x;
                const sy = asNumber(pos.y, 0) - shape.y;
                autoSeats.push(
                    <Circle key={`ds-${seatId}`} x={sx} y={sy} radius={seatRadius}
                        fill={seatFill} stroke={seatStroke} strokeWidth={1} listening={false} />
                );
            });
        } else if (seatCount > 0 && (shape.type === 'table_rect' || shape.type === 'bar' || shape.type === 'table_round')) {
            if (shape.type === 'table_round') {
                const cx = shape.width / 2;
                const cy = shape.height / 2;
                const ringR = Math.min(shape.width, shape.height) / 2 + seatGap;
                for (let i = 0; i < seatCount; i += 1) {
                    const angle = (i / seatCount) * Math.PI * 2 - Math.PI / 2;
                    autoSeats.push(
                        <Circle key={`as-${i}`} x={cx + Math.cos(angle) * ringR} y={cy + Math.sin(angle) * ringR}
                            radius={seatRadius} fill={seatFill} stroke={seatStroke} strokeWidth={1} listening={false} />
                    );
                }
            } else {
                // Distribute seats around a rect perimeter, weighted by side length
                const w = shape.width;
                const h = shape.height;
                const perim = 2 * (w + h);
                const positions = [];
                for (let i = 0; i < seatCount; i += 1) {
                    const t = (i + 0.5) / seatCount; // 0..1 around perimeter
                    let d = t * perim;
                    if (d <= w) {
                        positions.push({ x: d, y: -seatGap });
                    } else if (d <= w + h) {
                        positions.push({ x: w + seatGap, y: d - w });
                    } else if (d <= 2 * w + h) {
                        positions.push({ x: w - (d - (w + h)), y: h + seatGap });
                    } else {
                        positions.push({ x: -seatGap, y: h - (d - (2 * w + h)) });
                    }
                }
                positions.forEach((p, i) => autoSeats.push(
                    <Circle key={`as-${i}`} x={p.x} y={p.y} radius={seatRadius}
                        fill={seatFill} stroke={seatStroke} strokeWidth={1} listening={false} />
                ));
            }
        }

        // Use Group for all objects so the reconciler sees a single Konva node per object.
        // React.Fragment is not traversed by react-konva's reconciler and causes invisibility.
        const groupProps = {
            key: obj.id,
            ref,
            x: shape.x,
            y: shape.y,
            rotation: shape.rotation || 0,
            draggable: true,
            onClick: () => this.setState({ selectedId: obj.id }),
            onTap: () => this.setState({ selectedId: obj.id }),
            onDragEnd: (e) => this.onDragEnd(obj.id, e),
            onTransformEnd: (e) => this.onTransformEnd(obj.id, e),
        };

        if (shape.type === 'table_round') {
            const r = Math.min(shape.width, shape.height) / 2;
            return (
                <Group {...groupProps}>
                    <Circle x={r} y={r} radius={r}
                        fill={shape.fill || DEFAULT_COLORS.table_round}
                        stroke={stroke} strokeWidth={strokeWidth} />
                    <Text x={0} y={r - 8} width={shape.width} height={16}
                        text={shape.label} align="center" fill="#fff" fontSize={13} listening={false} />
                    {autoSeats}
                </Group>
            );
        }
        if (shape.type === 'table_rect' || shape.type === 'bar') {
            return (
                <Group {...groupProps}>
                    <Rect width={shape.width} height={shape.height}
                        fill={shape.fill || DEFAULT_COLORS[shape.type] || '#6d4c41'}
                        cornerRadius={4} stroke={stroke} strokeWidth={strokeWidth} />
                    {shape.label ? (
                        <Text x={0} y={shape.height / 2 - 8}
                            width={shape.width} height={16} text={shape.label}
                            align="center" fill="#fff" fontSize={13} listening={false} />
                    ) : null}
                    {autoSeats}
                </Group>
            );
        }
        if (shape.type === 'wall') {
            return (
                <Group {...groupProps}>
                    <Rect width={shape.width} height={shape.height}
                        fill={shape.fill || DEFAULT_COLORS.wall}
                        stroke={stroke} strokeWidth={strokeWidth} />
                </Group>
            );
        }
        if (shape.type === 'chair') {
            return (
                <Group {...groupProps}>
                    <Rect width={shape.width} height={shape.height}
                        fill={shape.fill || DEFAULT_COLORS.chair}
                        cornerRadius={4} stroke={stroke} strokeWidth={strokeWidth} />
                    {shape.label ? (
                        <Text x={0} y={shape.height / 2 - 6}
                            width={shape.width} height={12} text={shape.label}
                            align="center" fill="#fff" fontSize={10} listening={false} />
                    ) : null}
                </Group>
            );
        }
        if (shape.type === 'text') {
            return (
                <Group {...groupProps}>
                    <Text width={shape.width} height={shape.height}
                        text={shape.label} fill={shape.fill || '#ffffff'} fontSize={16}
                        stroke={stroke} strokeWidth={strokeWidth} />
                </Group>
            );
        }

        // Fallback for legacy or unknown layout object types: render them as
        // generic rectangles so persisted layout data never disappears.
        return (
            <Group {...groupProps}>
                <Rect width={shape.width} height={shape.height}
                    fill={shape.fill || '#6d4c41'}
                    cornerRadius={4} stroke={stroke} strokeWidth={strokeWidth} />
                {shape.label ? (
                    <Text x={0} y={shape.height / 2 - 8}
                        width={shape.width} height={16} text={shape.label}
                        align="center" fill="#fff" fontSize={13} listening={false} />
                ) : null}
            </Group>
        );
    };

    render() {
        const { width, height } = this.props;
        const {
            containerWidth,
            containerHeight,
            selectedId,
            snap,
            showGrid,
            objects,
        } = this.state;
        const selected = selectedId ? objects.find(o => o.id === selectedId) : null;

        const displayW = Math.min(900, Math.max(width || 0, 320));
        const displayH = Math.max(360, Math.min(640, Math.round((height || 800) * (displayW / Math.max(width || 1, 1)))));

        // Stage uses explicit displayW/displayH so it always renders, even before
        // ResizeObserver fires (e.g. while the modal is animating in). We still
        // honour measured dimensions when they exceed the inline size, but never
        // fall to zero and never gate Stage visibility on measurement timing.
        const stageW = Math.max(containerWidth || 0, displayW);
        const stageH = Math.max(containerHeight || 0, displayH);

        const margin = 12;
        const availW = Math.max(stageW - (margin * 2), 1);
        const availH = Math.max(stageH - (margin * 2), 1);
        const scale = Math.min(availW / Math.max(width || 1, 1), availH / Math.max(height || 1, 1));
        const offsetX = margin + ((availW - ((width || 0) * scale)) / 2);
        const offsetY = margin + ((availH - ((height || 0) * scale)) / 2);

        return (
            <div className={s.editorWrap}>
                {/* Toolbar */}
                <div className={s.toolbar}>
                    <div className={s.toolbarGroup}>
                        <span className={s.toolLabel}>Add:</span>
                        <ButtonGroup size="sm">
                            <Button outline color="secondary" onClick={() => this.addObject((objs) => makeTable(objs, 'rect'))}>
                                <i className="zmdi zmdi-stop" /> Rect Table
                            </Button>
                            <Button outline color="secondary" onClick={() => this.addObject((objs) => makeTable(objs, 'round'))}>
                                <i className="zmdi zmdi-circle-o" /> Round Table
                            </Button>
                            <Button outline color="secondary" onClick={() => this.addObject((objs) => makeBar(objs))}>
                                <i className="zmdi zmdi-minus" /> Bar
                            </Button>
                            <Button outline color="secondary" onClick={() => this.addObject((objs) => makeWall(objs))}>
                                <i className="zmdi zmdi-border-horizontal" /> Wall
                            </Button>
                            <Button outline color="secondary" onClick={() => this.addObject((objs) => makeChair(objs))}>
                                <i className="zmdi zmdi-square-o" /> Chair
                            </Button>
                            <Button outline color="secondary" onClick={() => this.addObject((objs) => makeText(objs))}>
                                <i className="zmdi zmdi-format-size" /> Text
                            </Button>
                        </ButtonGroup>
                    </div>
                    <div className={s.toolbarGroup}>
                        <ButtonGroup size="sm">
                            <Button outline color="secondary" disabled={!selectedId} onClick={this.duplicateSelected} title="Duplicate">
                                <i className="zmdi zmdi-copy" />
                            </Button>
                            <Button outline color="secondary" disabled={!selectedId} onClick={this.bringToFront} title="Bring to front">
                                <i className="zmdi zmdi-flip-to-front" />
                            </Button>
                            <Button outline color="secondary" disabled={!selectedId} onClick={this.sendToBack} title="Send to back">
                                <i className="zmdi zmdi-flip-to-back" />
                            </Button>
                            <Button outline color="danger" disabled={!selectedId} onClick={this.deleteSelected} title="Delete">
                                <i className="zmdi zmdi-delete" />
                            </Button>
                        </ButtonGroup>
                        <label className={s.snapToggle}>
                            <input type="checkbox" checked={snap}
                                onChange={(e) => this.setState({ snap: e.target.checked })} />
                            {' '}Snap
                        </label>
                        <label className={s.snapToggle}>
                            <input type="checkbox" checked={showGrid}
                                onChange={(e) => {
                                    localStorage.setItem('layoutEditor_showGrid', e.target.checked);
                                    this.setState({ showGrid: e.target.checked });
                                }} />
                            {' '}Grid
                        </label>
                    </div>
                </div>

                <div className={s.editorBody}>
                    {/* Canvas */}
                    <div className={s.canvasWrap}
                        ref={this.containerRef}
                        style={{ width: displayW, height: displayH, cursor: 'crosshair' }}>
                        {scale > 0 && (
                            <Stage
                                ref={this.stageRef}
                                width={stageW}
                                height={stageH}
                                onMouseDown={(e) => {
                                    if (e.target === e.target.getStage()) {
                                        this.setState({ selectedId: null });
                                    }
                                }}
                            >
                                {/* Grid layer */}
                                <Layer listening={false}>
                                    <Rect width={stageW} height={stageH} fill="#1e1e2e" />
                                    <Rect
                                        x={offsetX}
                                        y={offsetY}
                                        width={Math.max((width || 0) * scale, 1)}
                                        height={Math.max((height || 0) * scale, 1)}
                                        fill="#2a2a3a"
                                        stroke="rgba(255,255,255,0.08)"
                                        strokeWidth={1}
                                    />
                                </Layer>
                                {showGrid && (
                                    <Layer listening={false}>
                                        <Group x={offsetX} y={offsetY} scaleX={scale} scaleY={scale}>
                                            {this.renderGrid(width, height)}
                                        </Group>
                                    </Layer>
                                )}
                                {/* Objects layer */}
                                <Layer>
                                    <Group x={offsetX} y={offsetY} scaleX={scale} scaleY={scale}>
                                        {objects.map(this.renderObject)}
                                    </Group>
                                    <Transformer
                                        ref={this.transformerRef}
                                        rotateEnabled={true}
                                        borderStroke="#ffd600"
                                        anchorStroke="#ffd600"
                                        anchorFill="#ffd600"
                                        anchorSize={8}
                                        enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right', 'middle-left', 'middle-right']}
                                    />
                                </Layer>
                            </Stage>
                        )}
                    </div>

                    {/* Properties panel */}
                    <div className={s.propsPanel}>
                        {!selected ? (
                            <div className={s.noSelection}>Select an object to edit its properties</div>
                        ) : (
                            <div className={s.propsList}>
                                <div className={s.propRow}>
                                    <Label className={s.propLabel}>Label</Label>
                                    <Input bsSize="sm" value={selected.label}
                                        onChange={(e) => this.updateSelectedField('label', e.target.value)} />
                                </div>
                                <div className={s.propRowDouble}>
                                    {(normalizeType(selected.type) === 'table_rect' || normalizeType(selected.type) === 'table_round' || normalizeType(selected.type) === 'chair') ? (
                                        <div className={s.propHalf}>
                                            <Label className={s.propLabel}>Seats</Label>
                                            <Input bsSize="sm" type="number" min={1} max={30}
                                                value={selected.seatCount}
                                                onChange={(e) => this.updateSelectedField('seatCount', parseInt(e.target.value, 10) || 0)} />
                                        </div>
                                    ) : <div className={s.propHalf} />}
                                    <div className={s.propHalf}>
                                        <Label className={s.propLabel}>Fill</Label>
                                        <input type="color" className={s.colorInput}
                                            value={selected.fill || '#888888'}
                                            onChange={(e) => this.updateSelectedField('fill', e.target.value)} />
                                    </div>
                                </div>
                                <div className={s.propRowDouble}>
                                    <div className={s.propHalf}>
                                        <Label className={s.propLabel}>X</Label>
                                        <Input bsSize="sm" type="number"
                                            value={selected.x}
                                            onChange={(e) => this.updateSelectedField('x', parseInt(e.target.value, 10) || 0)} />
                                    </div>
                                    <div className={s.propHalf}>
                                        <Label className={s.propLabel}>Y</Label>
                                        <Input bsSize="sm" type="number"
                                            value={selected.y}
                                            onChange={(e) => this.updateSelectedField('y', parseInt(e.target.value, 10) || 0)} />
                                    </div>
                                </div>
                                <div className={s.propRowDouble}>
                                    <div className={s.propHalf}>
                                        <Label className={s.propLabel}>W</Label>
                                        <Input bsSize="sm" type="number" min={GRID}
                                            value={selected.width}
                                            onChange={(e) => this.updateSelectedField('width', parseInt(e.target.value, 10) || GRID)} />
                                    </div>
                                    <div className={s.propHalf}>
                                        <Label className={s.propLabel}>H</Label>
                                        <Input bsSize="sm" type="number" min={GRID}
                                            value={selected.height}
                                            onChange={(e) => this.updateSelectedField('height', parseInt(e.target.value, 10) || GRID)} />
                                    </div>
                                </div>
                                <div className={s.propRow}>
                                    <Label className={s.propLabel}>Rotation</Label>
                                    <Input bsSize="sm" type="number"
                                        value={Math.round(selected.rotation || 0)}
                                        onChange={(e) => this.updateSelectedField('rotation', parseInt(e.target.value, 10) || 0)} />
                                </div>
                                <div className={s.propRow}>
                                    <Label className={s.propLabel}>Type</Label>
                                    <div className={s.propValue}>{selected.type}</div>
                                </div>
                                <div className={s.propRow}>
                                    <Label className={s.propLabel}>Id</Label>
                                    <div className={s.propValue}>{selected.id}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default LayoutCanvasEditor;
